'use client';

/**
 * DemoRulesCard — declarative rerank rules fired on this run.
 *
 * Each named rule self-describes: name (mono identifier), description
 * (what it does), factor (× 1.05 boost / × 0.40 penalty), affected
 * count (how many items it touched), and optional `reason` ("fired
 * because: enriched_type='chair' matched 0/40"). Penalty rules
 * (factor < 1) red-tint to signal the substrate caught retrieval
 * drift.
 *
 * Picks the latest ACT scene whose `rerank_rules` array is non-empty
 * (fresh discovery turns + sparse-recast turns fire rules; refinement
 * turns reuse the cached pool and leave them empty).
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useQuery } from '@tanstack/react-query';
import { Filter, TrendingUp, TrendingDown, ShieldAlert } from 'lucide-react';
import { api } from '@/lib/api/client';
import { useDemoAskStore } from '@/lib/stores/demo-ask-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import type { TestRunView, RuleApplication } from './demo-test-run-types';


export function DemoRulesCard() {
  const T = useCardTypography();
  const simulationId = useDemoAskStore((s) => s.testRun?.simulation_id ?? null);

  const { data } = useQuery<TestRunView>({
    queryKey: ['test_run_view', simulationId],
    queryFn: async () => {
      const res = await api.getStrategyData<TestRunView>('test_run_view', {
        simulation_id: simulationId,
      });
      if (!res.success) throw new Error(res.error || 'test_run_view failed');
      return res.data;
    },
    enabled: Boolean(simulationId),
    staleTime: 30_000,
  });

  if (!simulationId || !data) return null;

  const sceneWithRules = [...data.scenes]
    .reverse()
    .find((s) => s.rerank_rules && s.rerank_rules.length > 0);
  if (!sceneWithRules || !sceneWithRules.rerank_rules) return null;

  const rules: RuleApplication[] = sceneWithRules.rerank_rules;

  return (
    <div className="bg-card/30 border border-border rounded-2xl px-5 py-4 mb-4">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <div className={cn(T.body, 'text-sm font-medium')}>Rules engine</div>
          <CognitionBadge mode="fast" />
        </div>
        <div className={cn(T.metadata, 'text-muted-foreground text-xs')}>
          {rules.length} fired
        </div>
      </div>
      <p className={cn(T.metadata, 'text-muted-foreground text-xs mb-3')}>
        After source blending, declarative rules adjust rankings. Each named, self-describing, audited.
      </p>

      <div className="space-y-1.5">
        {rules.map((rule, idx) => (
          <RuleRow key={`${rule.name}-${idx}`} rule={rule} />
        ))}
      </div>
    </div>
  );
}


function RuleRow({ rule }: { rule: RuleApplication }) {
  // RuleApplication is polymorphic. Two shapes:
  //  1. Rerank rule  — has factor (boost / penalty)
  //  2. Gate rule    — has threshold + top_score + action (no factor)
  // Branch on factor presence to render the right row treatment.
  if (rule.factor !== null) {
    return <RerankRuleRow rule={rule} />;
  }
  return <GateRuleRow rule={rule} />;
}


function RerankRuleRow({ rule }: { rule: RuleApplication }) {
  const T = useCardTypography();
  const factor = rule.factor as number;        // narrowed by caller
  const isPenalty = factor < 1.0;
  const Icon = isPenalty ? TrendingDown : TrendingUp;

  return (
    <div className={cn(
      'grid grid-cols-[auto_1fr_auto_auto] gap-3 items-start px-3 py-2 rounded-lg',
      isPenalty ? 'bg-destructive/5' : 'bg-muted/30',
    )}>
      <Icon className={cn(
        'w-4 h-4 mt-0.5 shrink-0',
        isPenalty ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400',
      )} />
      <div className="min-w-0">
        <div className={cn(T.body, 'text-xs font-mono font-medium', isPenalty && 'text-destructive')}>
          {rule.name}
        </div>
        {rule.description && (
          <div className={cn(T.metadata, 'text-muted-foreground text-[11px] leading-snug mt-0.5')}>
            {rule.description}
          </div>
        )}
        {rule.reason && (
          <div className={cn(
            T.metadata,
            'text-[11px] italic mt-1',
            isPenalty ? 'text-destructive/80' : 'text-muted-foreground',
          )}>
            fired because: {rule.reason}
          </div>
        )}
      </div>
      <div className={cn(
        'font-mono text-xs font-medium tabular-nums',
        isPenalty ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400',
      )}>
        × {factor.toFixed(2).replace(/\.00$/, '')}
      </div>
      <div className={cn(T.metadata, 'text-muted-foreground text-[11px] tabular-nums min-w-[60px] text-right')}>
        {rule.affected ?? 0} {(rule.affected ?? 0) === 1 ? 'item' : 'items'}
      </div>
    </div>
  );
}


function GateRuleRow({ rule }: { rule: RuleApplication }) {
  const T = useCardTypography();
  // Gates always tint as "noteworthy" — they fire to signal a problem
  // (no_match_gate) rather than a routine ranking nudge.
  return (
    <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-start px-3 py-2 rounded-lg bg-amber-500/10">
      <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="min-w-0">
        <div className={cn(T.body, 'text-xs font-mono font-medium text-amber-700 dark:text-amber-300')}>
          {rule.name}
        </div>
        {rule.description && (
          <div className={cn(T.metadata, 'text-amber-700/80 dark:text-amber-300/80 text-[11px] leading-snug mt-0.5')}>
            {rule.description}
          </div>
        )}
        {rule.action && (
          <div className={cn(T.metadata, 'text-amber-700 dark:text-amber-300 text-[11px] mt-1')}>
            action: {rule.action}
          </div>
        )}
        {rule.reason && (
          <div className={cn(T.metadata, 'text-amber-700/80 dark:text-amber-300/80 text-[11px] italic mt-1')}>
            fired because: {rule.reason}
          </div>
        )}
      </div>
      <div className={cn(T.metadata, 'text-amber-700 dark:text-amber-300 text-[11px] font-mono tabular-nums text-right whitespace-nowrap')}>
        {rule.top_score !== null && rule.threshold !== null && (
          <>top {rule.top_score.toFixed(2)} / thr {rule.threshold.toFixed(2)}</>
        )}
      </div>
    </div>
  );
}


function CognitionBadge({ mode }: { mode: 'llm' | 'fast' }) {
  const styles = mode === 'llm'
    ? 'bg-violet-500/15 text-violet-700 dark:text-violet-300'
    : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  return (
    <span className={cn('font-mono text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider', styles)}>
      cognition: {mode}
    </span>
  );
}
